// PAINT BUCKET flood fill: recolor the face-connected region of same-coloured
// voxels reachable from the clicked voxel to the active palette index. Unlike
// the Paint tool (which recolors only what the cursor sweeps), one click fills a
// whole contiguous blob — a 6-connected BFS over cells that share the seed's
// palette index, stopping at any cell with a different colour, an empty cell, or
// the work-volume bounds.
//
// This is a single-shot operation: the tool calls it inside the already-open
// grid transaction (see app/tool-registry + main's beginGridAction), so every
// grid.set here is recorded as one undoable edit and no drag/commit is needed.

import { type PaletteIndex, type VoxelGrid } from "../../model/voxel-grid";
import { type CellBounds, type PickTarget, cellInBounds } from "../pick";

// 6-connected neighbour offsets (face adjacency — diagonals don't count, so the
// fill spreads through solid contact the way a 2D bucket spreads through pixels).
const NEIGHBORS: ReadonlyArray<readonly [number, number, number]> = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
  [0, 0, -1],
];

export function bucketFill(
  pick: PickTarget,
  grid: VoxelGrid,
  bounds: CellBounds,
  paletteIndex: PaletteIndex,
  global = false, // true = recolor EVERY voxel of the seed colour (Shift-click)
): void {
  // The bucket recolors an EXISTING voxel and its like-coloured neighbours, so a
  // bare surface (empty cell / floor) has nothing to seed from — no-op.
  if (!pick || pick.kind !== "voxel") return;
  const src = grid.get(pick.ix, pick.iy, pick.iz);
  if (src === 0) return; // empty seed — nothing to fill
  if (src === paletteIndex) return; // region already the target colour

  // GLOBAL: recolor every voxel sharing the seed colour anywhere in the object,
  // ignoring connectivity (the MagicaVoxel "replace all of this colour" mode).
  // Collect first, then set — mutating while iterating entries() is unsafe.
  if (global) {
    const targets: Array<[number, number, number]> = [];
    for (const [ix, iy, iz, p] of grid.entries()) {
      if (p === src) targets.push([ix, iy, iz]);
    }
    for (const [ix, iy, iz] of targets) grid.set(ix, iy, iz, paletteIndex);
    return;
  }

  // CONNECTED: BFS with a head pointer (not Array.shift) so a large blob stays O(n).
  const visited = new Set<string>([`${pick.ix},${pick.iy},${pick.iz}`]);
  const queue: Array<[number, number, number]> = [[pick.ix, pick.iy, pick.iz]];
  for (let head = 0; head < queue.length; head++) {
    const [ix, iy, iz] = queue[head];
    grid.set(ix, iy, iz, paletteIndex);
    for (const [dx, dy, dz] of NEIGHBORS) {
      const nx = ix + dx;
      const ny = iy + dy;
      const nz = iz + dz;
      const key = `${nx},${ny},${nz}`;
      if (visited.has(key)) continue;
      if (!cellInBounds(nx, ny, nz, bounds)) continue;
      if (grid.get(nx, ny, nz) !== src) continue;
      visited.add(key);
      queue.push([nx, ny, nz]);
    }
  }
}
